import { Check } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { SectionTag } from "./About";

function NetworkDiagram() {
  // central core node + 6 satellite nodes connected by glowing lines
  const nodes = [
    { x: 80, y: 80 },
    { x: 320, y: 60 },
    { x: 540, y: 90 },
    { x: 80, y: 320 },
    { x: 540, y: 320 },
    { x: 320, y: 360 },
  ];
  const center = { x: 310, y: 210 };

  return (
    <svg
      viewBox="0 0 620 420"
      className="w-full h-auto"
      data-testid="network-diagram"
    >
      <defs>
        <radialGradient id="core" cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#00F0FF" stopOpacity="0.95" />
          <stop offset="1" stopColor="#00F0FF" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="line" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#00F0FF" stopOpacity="0.05" />
          <stop offset="0.5" stopColor="#00F0FF" stopOpacity="0.7" />
          <stop offset="1" stopColor="#00F0FF" stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* grid background */}
      {Array.from({ length: 12 }).map((_, i) => (
        <line
          key={`v${i}`}
          x1={i * 56}
          y1="0"
          x2={i * 56}
          y2="420"
          stroke="rgba(0,240,255,0.06)"
        />
      ))}
      {Array.from({ length: 9 }).map((_, i) => (
        <line
          key={`h${i}`}
          x1="0"
          y1={i * 52}
          x2="620"
          y2={i * 52}
          stroke="rgba(0,240,255,0.06)"
        />
      ))}

      {/* connection lines */}
      {nodes.map((n, i) => (
        <line
          key={i}
          x1={center.x}
          y1={center.y}
          x2={n.x}
          y2={n.y}
          stroke="url(#line)"
          strokeWidth="1.5"
          className="wt-dash"
        />
      ))}

      {/* core glow */}
      <circle cx={center.x} cy={center.y} r="80" fill="url(#core)" opacity="0.5" />
      <circle
        cx={center.x}
        cy={center.y}
        r="14"
        fill="#00F0FF"
        className="wt-pulse"
      />
      <circle
        cx={center.x}
        cy={center.y}
        r="26"
        fill="none"
        stroke="#00F0FF"
        strokeOpacity="0.6"
      />

      {/* satellite nodes */}
      {nodes.map((n, i) => (
        <g key={i}>
          <rect
            x={n.x - 14}
            y={n.y - 14}
            width="28"
            height="28"
            fill="#0A1428"
            stroke="#00F0FF"
            strokeOpacity="0.7"
          />
          <rect
            x={n.x - 6}
            y={n.y - 6}
            width="12"
            height="12"
            fill="#00F0FF"
            opacity="0.85"
          />
        </g>
      ))}
    </svg>
  );
}

export default function Networking() {
  const { t } = useLang();
  return (
    <section
      id="network"
      className="relative py-24 sm:py-32 border-t border-white/5 bg-[#050B14] overflow-hidden"
      data-testid="networking-section"
    >
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 80% 0%, rgba(0,240,255,0.08), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionTag>{t.network.tag}</SectionTag>
          <h2
            className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-[1.1]"
            data-testid="networking-title"
          >
            {t.network.title}
          </h2>
          <p className="mt-5 text-slate-200 text-base sm:text-lg max-w-xl leading-[1.75]">
            {t.network.sub}
          </p>

          <ul className="mt-10 grid sm:grid-cols-2 gap-5">
            {t.network.bullets.map((b, i) => (
              <li
                key={i}
                className="border border-[rgba(0,240,255,0.15)] p-5 hover:border-[rgba(0,240,255,0.4)] transition-colors"
                data-testid={`network-bullet-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 inline-flex items-center justify-center bg-[#00F0FF] text-[#050B14]">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <h3 className="font-display text-base font-semibold text-white">
                    {b.title}
                  </h3>
                </div>
                <p className="mt-3 text-sm sm:text-[15px] text-slate-300 leading-[1.75]">
                  {b.desc}
                </p>
              </li>
            ))}
          </ul>
        </div>

        <div className="relative">
          <div className="border border-[rgba(0,240,255,0.18)] bg-[#0A1428] p-6 sm:p-10 wt-glow-border">
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono-tech text-[11px] tracking-[0.3em] uppercase text-[#00F0FF]">
                CORE / NODES
              </span>
              <span className="flex items-center gap-2 text-xs text-slate-400 font-mono-tech">
                <span className="w-2 h-2 rounded-full bg-[#00F0FF] wt-pulse" />
                LIVE
              </span>
            </div>
            <NetworkDiagram />
          </div>
          <div className="absolute -bottom-3 -end-3 w-24 h-24 border border-[#00F0FF]/30 pointer-events-none" />
        </div>
      </div>
    </section>
  );
}
